import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Row, Col, Card, CardHeader } from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles.css";

// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  getCompany,
  updateCompany,
  deleteCompany,
  companyResetError,
  companyUpdateStatusState,
} from "../../../store/actions";
import {
  INVITE_COMPANY,
  DELETE_COMPANY,
  COMPANY_INVITE_STATUS,
  COMPANY_DELETE_STATUS,
  GET_COMPANY,
  GET_COMPANY_LIST_TO_SWITCH,
  UPDATE_COMPANY,
} from "../../../store/company/actionType";

// Types
import {
  UnifiedTenant,
  DisplayStatus,
} from "../../../types/tenant";

// Components
import TableContainer, {
  SelectFilterOptionGroup,
} from "../../../components/Common/TableContainer";
import TenantModal from "../../../components/Common/TenantModal";
import DeleteClientModal from "../../../components/Common/DeleteModal";
import ConnectionStatusModal from "../../../components/Common/ConnectionStatusModal";
import ToastError from "../../../components/Common/ToastError";
import { Column } from "react-table";

// Current user role
import { getUserRole } from "../../Users/api/usersApi";
import {
  CountInfoTip,
  UsageSummary,
  planLabel,
} from "../../Usage/UsageParts";
import { formatDate } from "../../Usage/usageFormat";
import UsageExportModal from "../../Usage/UsageExportModal";
import {
  getAdminUsage,
  getCurrentMonth,
  subscribeSendingChanges,
} from "../../Usage/usageApi";

interface PageParams {
  rowsPerPage: number;
  currentPage: number;
}

interface FilterParams {
  search: string;
  status: string; // eslint-disable-line no-restricted-globals
}

interface CustomerToDelete {
  id?: string;
  name?: string; // eslint-disable-line no-restricted-globals
}

interface CustomerConnectionStatus {
  id: string;
  connectionStatus: string;
}

const Customers: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t, i18n } = useTranslation();

  // Get current user role for permission checks
  const currentUserRole = getUserRole();
  const isSuperAdmin = currentUserRole === "SUPER_ADMIN";

  /* Modal States */
  const [addClientModal, setAddClientModal] = useState<boolean>(false);
  const [deleteClientModal, setDeleteClientModal] = useState<boolean>(false);
  const [connectionStatusModal, setConnectionStatusModal] =
    useState<boolean>(false);

  const [customerToDelete, setCustomerToDelete] = useState<CustomerToDelete>(
    {}
  );
  const [showExport, setShowExport] = useState<boolean>(false);
  // This month's plan, usage and sending status per tenant id, from
  // GET /admin/usage, merged into the tenant list on the page.
  const [usageById, setUsageById] = useState<Record<string, any>>({});
  const [earliestMonth, setEarliestMonth] = useState<string | undefined>();
  // Note: customerConnectionStatus and setCustomerConnectionStatus are used by ConnectionStatusModal
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customerConnectionStatus, setCustomerConnectionStatus] =
    useState<CustomerConnectionStatus>({
      id: "",
      connectionStatus: "",
    });

  // Query parameters for PAGE SIZE and PAGE INDEX
  const [pageParams, setPageParamsState] = useState<PageParams>({
    rowsPerPage: 10,
    currentPage: 1,
  });

  // Wrapper to handle partial updates
  const setPageParams = (params: Partial<PageParams>) => {
    setPageParamsState((prev) => ({ ...prev, ...params }));
  };

  // Query parameters for SEARCH TERM, DATE, STATUS
  const [filterParams, setFilterParams] = useState<FilterParams>({
    search: "",
    status: "",
  });

  const {
    company,
    isCompanyLoading,
    companyError,
    companyListToSwitchError,
    companyUpdateError,
    pageCount,
    hasPreviousPage,
    hasNextPage,
    isChannelLoading,
  } = useSelector((state: any) => ({
    company: state.Company.company,
    isCompanyLoading: state.Company.isCompanyLoading,
    companyError: state.Company.companyError,
    companyListToSwitchError: state.Company.companyListToSwitchError,
    companyUpdateError: state.Company.companyUpdateError,
    pageCount: state.Company.pageCount,
    hasPreviousPage: state.Company.hasPreviousPage,
    hasNextPage: state.Company.hasNextPage,
    isChannelLoading: state.Channel.isChannelLoading,
  }));

  useEffect(() => {
    // "paused" is a sending status, not an account status: the tenant list
    // API does not know it, so it is filtered on the page.
    const status = filterParams.status === "paused" ? "" : filterParams.status;
    dispatch(getCompany({ ...pageParams, ...filterParams, status }) as any);
  }, [dispatch, pageParams, filterParams, isChannelLoading]);

  useEffect(() => {
    const load = () =>
      getAdminUsage(getCurrentMonth())
        .then((data: any) => {
          setUsageById(
            Object.fromEntries(data.tenants.map((row: any) => [row.id, row]))
          );
          setEarliestMonth(data.earliestMonth);
        })
        .catch(() => setUsageById({}));
    load();
    return subscribeSendingChanges(load);
  }, [company]);

  const handleConfirmDeleteClient = () => {
    if (customerToDelete?.id) {
      dispatch(deleteCompany(customerToDelete.id) as any);
    }
  };

  const handleCloseInviteClientModal = () => {
    dispatch(companyUpdateStatusState(COMPANY_INVITE_STATUS, "idle") as any);
    dispatch(companyResetError(INVITE_COMPANY) as any);
    setAddClientModal(false);
  };

  const handleCloseDeleteClientModal = () => {
    setCustomerToDelete({});
    dispatch(companyUpdateStatusState(COMPANY_DELETE_STATUS, "idle") as any);
    dispatch(companyResetError(DELETE_COMPANY) as any);
    setDeleteClientModal(false);
  };

  const handleAddClient = () => {
    setAddClientModal(true);
  };

  // Helper function to render status badge (simplified)
  const getStatusBadge = (status: DisplayStatus): React.ReactElement => {
    const normalized = (status || "").toLowerCase() as DisplayStatus;
    const statusConfig: Record<
      DisplayStatus,
      { label: string; className: string }
    > = {
      active: { label: t("common.userStatus.ACTIVE"), className: "active" },
      inactive: {
        label: t("common.userStatus.INACTIVE"),
        className: "inactive-status",
      },
      pending: { label: t("common.userStatus.PENDING"), className: "pending" },
      expired: { label: t("common.userStatus.EXPIRED"), className: "expired" },
    };
    const config = statusConfig[normalized] || statusConfig.inactive;
    return (
      <span className={`status-badge ${config.className}`}>{config.label}</span>
    );
  };

  const handleConfirmConnectionStatus = () => {
    if (
      customerConnectionStatus?.id !== "" &&
      customerConnectionStatus?.connectionStatus !== ""
    ) {
      dispatch(
        updateCompany(customerConnectionStatus.id, {
          state: customerConnectionStatus.connectionStatus,
        }) as any
      );
    }
  };

  const resetToastError = (errorType: string) => {
    dispatch(companyResetError(errorType) as any);
  };

  // Map company data to UnifiedTenant format
  // Note: Backend now returns data in the correct format, so we just pass it through
  const mapToUnifiedTenant = (item: any): UnifiedTenant => {
    return {
      id: item.id,
      type: item.type || "tenant",
      portalId: item.portalId || "",
      smsSettingsUID: item.smsSettingsUID,
      name: item.name || "",
      handle: item.handle,
      status: item.status || "inactive",
      tenantStatus: item.tenantStatus || "INACTIVE",
      inviteStatus: item.inviteStatus,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  };

  const usageOf = (tenant: UnifiedTenant) => usageById[tenant.id] || {};
  // Paused only matters while the account is active.
  const isPausedBadge = (tenant: UnifiedTenant) =>
    usageOf(tenant).sendingStatus === "paused" &&
    (tenant.status || "").toLowerCase() === "active";

  const tenantListColumns: Column<UnifiedTenant>[] = [
    {
      Header: t("tenants.list.columns.tenantName"),
      accessor: "name",
      width: "14%",
      Cell: ({ value }: any) => <div className="fw-medium">{value}</div>,
    },
    {
      Header: t("tenants.list.columns.portalId"),
      accessor: "portalId",
      width: "13%",
      Cell: ({ value }: any) => <div>{value || "-"}</div>,
    },
    {
      Header: t("usage.columns.plan"),
      id: "plan",
      width: "12%",
      Cell: ({ row }: any) => planLabel(t, usageOf(row.original).plan),
    },
    {
      Header: (
        <>
          {t("tenants.list.columns.usage")}
          <CountInfoTip />
        </>
      ),
      id: "usage",
      width: "21%",
      Cell: ({ row }: any) => {
        const usage = usageOf(row.original);
        return (
          <UsageSummary
            messageCount={usage.messageCount || 0}
            quota={usage.quota}
          />
        );
      },
    },
    {
      Header: t("usage.columns.contractEnd"),
      id: "contractEnd",
      width: "12%",
      Cell: ({ row }: any) => {
        const contractEnd = usageOf(row.original).contractEnd;
        return contractEnd
          ? formatDate(contractEnd, i18n.language)
          : t("usage.notSet");
      },
    },
    {
      Header: (
        <div style={{ textAlign: "center" }}>{t("common.labels.status")}</div>
      ),
      accessor: "status",
      width: "13%",
      Cell: ({ row }: any) => {
        const tenant = row.original as UnifiedTenant;
        return (
          <div style={{ textAlign: "center" }}>
            {/* One badge per row. Paused shows only for active tenants:
                inactive comes first, and invited tenants are not sending
                yet. The details page shows both statuses. */}
            {isPausedBadge(tenant) ? (
              <span className="status-badge paused">
                {t("tenants.details.sendingControl.status.paused")}
              </span>
            ) : (
              getStatusBadge(tenant.status)
            )}
          </div>
        );
      },
    },
  ];

  // Simplified status filter options
  const accountStatusOptions: SelectFilterOptionGroup[] = [
    {
      options: [
        { label: t("common.labels.all"), value: "all" },
        { label: t("common.userStatus.ACTIVE"), value: "active" },
        { label: t("common.userStatus.INACTIVE"), value: "inactive" },
        { label: t("common.userStatus.PENDING"), value: "pending" },
        { label: t("common.userStatus.EXPIRED"), value: "expired" },
        {
          label: t("tenants.details.sendingControl.status.paused"),
          value: "paused",
        },
      ],
    },
  ];

  // Map company data to unified format
  const unifiedTenants: UnifiedTenant[] = (
    Array.isArray(company) ? company.map(mapToUnifiedTenant) : []
  ).filter(
    (tenant: UnifiedTenant) =>
      filterParams.status !== "paused" ||
      isPausedBadge(tenant)
  );

  // Calculate total count
  const totalCount = unifiedTenants.length;


  document.title = t("common.pageTitle", { page: t("tenants.pageTitle") });

  return (
    <React.Fragment>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {showExport && (
        <UsageExportModal
          earliestMonth={earliestMonth}
          onClose={() => setShowExport(false)}
        />
      )}
      <TenantModal
        show={addClientModal}
        onCloseClick={() => {
          handleCloseInviteClientModal();
        }}
        mode="create"
      />
      <DeleteClientModal
        show={deleteClientModal}
        onDeleteClick={handleConfirmDeleteClient}
        onCloseClick={() => {
          handleCloseDeleteClientModal();
        }}
        customerToDelete={customerToDelete}
      />
      <ConnectionStatusModal
        show={connectionStatusModal}
        onConfirmConnectionStatus={handleConfirmConnectionStatus}
        onCloseClick={() => {
          setConnectionStatusModal(false);
        }}
      />

      {companyError && (
        <ToastError
          error={companyError}
          resetError={() => resetToastError(GET_COMPANY)}
        />
      )}
      {companyListToSwitchError && (
        <ToastError
          error={companyListToSwitchError}
          resetError={() => resetToastError(GET_COMPANY_LIST_TO_SWITCH)}
        />
      )}
      {companyUpdateError && (
        <ToastError
          error={companyUpdateError}
          resetError={() => resetToastError(UPDATE_COMPANY)}
        />
      )}

      <Row>
        <Col lg={12}>
          <Card id="customerList">
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <div className="col-sm">
                  <div>
                    <h5 className="card-title mb-0">
                      {t("tenants.list.heading", { count: totalCount })}
                    </h5>
                  </div>
                </div>
                <div className="col-sm-auto d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary add-btn"
                    onClick={() => setShowExport(true)}
                    style={{
                      backgroundColor: "#ffffff",
                      color: "#063a5e",
                      borderColor: "#063a5e",
                    }}
                  >
                    <i className="ri-download-2-line align-bottom me-1"></i>
                    {t("tenants.list.exportUsage")}
                  </button>
                {isSuperAdmin && (
                  <div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-primary add-btn"
                        id="create-btn"
                        onClick={() => handleAddClient()}
                        style={{
                          backgroundColor: "#ffffff",
                          color: "#063a5e",
                          borderColor: "#063a5e",
                        }}
                      >
                        <i className="ri-user-add-fill align-bottom me-1"></i>
                        {t("tenants.list.createTenant")}
                      </button>
                    </div>
                  </div>
                )}
                </div>
              </Row>
            </CardHeader>

            <div className="card-body pt-0">
              <div>
                <TableContainer
                  columns={tenantListColumns}
                  data={unifiedTenants}
                  loading={isCompanyLoading}
                  pageCount={pageCount}
                  hasPreviousPage={hasPreviousPage}
                  hasNextPage={hasNextPage}
                  pageParams={pageParams}
                  setPageParams={setPageParams}
                  filterParams={filterParams}
                  setFilterParams={setFilterParams}
                  disableRowModal={true}
                  onRowClick={(tenant: UnifiedTenant) =>
                    history.push(`/tenants/${tenant.id}`)
                  }
                  datePicker={false}
                  selectFilterOptions={accountStatusOptions}
                  isGlobalFilter={true}
                  isGlobalSearch={true}
                  tableClass="custom-header-css"
                  theadClass="text-muted"
                  trClass={undefined}
                  thClass={undefined}
                  divClass={undefined}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Customers;
